import React from 'react';
import {
  MessageSquare,
  FolderOpen,
  Mic,
  ChevronRight,
  FilePenLine,
  LayoutGrid,
  Users,
  Sparkles,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { PortalHomeRecentItem, PortalView } from '../portalNavigation';
import { hasMatrixRoom, PORTAL_NAV_MATRIX_LABEL, portalHomeZuletztItems } from '../portalNavigation';
import { PORTAL_SHELL } from '../portalTheme';
import { portalHomeSurfaceClass } from '../portalAppearance';
import { usePortalAppearance } from '../portalAppearanceContext';
import { PortalSnapshotStamps } from '../PortalSnapshotStamps';
import { ARKO_HUB_MODES, dispatchArkoHubIntent, type ArkoHubMode } from '../arkoHubModes';

export interface HomeViewProps {
  customerName: string;
  kanzlei: string;
  isPremium: boolean;
  matrixRoomId?: string;
  /** Eigenes Element — sonst kein Sprung in den Kanzlei-Chat. */
  elementSeat?: boolean;
  recentItems: PortalHomeRecentItem[];
  onNavigate: (view: PortalView) => void;
  onRecentItemClick: (item: PortalHomeRecentItem) => void;
  onArkoModeSelect?: (mode: ArkoHubMode) => void;
}

const RECENT_ICON: Record<PortalHomeRecentItem['kind'], LucideIcon> = {
  document: FilePenLine,
  arko: MessageSquare,
  view: LayoutGrid,
};

/** Schwarzer Grund, feine lila Kante — wie die Akte. Dunkel bleibt dieser Look. */
const HOME_GROUND = 'bg-[#07070a]';
const HOME_EDGE = 'rounded-lg border border-violet-400/30 bg-[#07070a]';
const HOME_CARD =
  'group text-left rounded-lg border border-violet-400/30 bg-[#07070a] p-4 transition-colors hover:border-violet-400/55';

/** Hell: warmer Stein, angehobene Fläche, Flieder tiefer. Kein Weiß. */
const HOME_LIGHT_GROUND = 'bg-[#e6e1d8] text-[#1c1915]';
const HOME_LIGHT_EDGE = 'rounded-lg border border-[#6a5684] bg-[#e6e1d8]';
const HOME_LIGHT_CARD =
  'group text-left rounded-lg border border-[#6a5684] bg-[#f3efe8] p-4 transition-colors hover:border-[#5b4678]';
const HOME_LIGHT_WELL = 'border border-[#6a5684] bg-[#f3efe8]';
const HOME_LIGHT_TITLE = 'text-[#1c1915]';
const HOME_LIGHT_MUTED = 'text-[#3f3832]';
const HOME_LIGHT_ACCENT = 'text-[#5b4678]';

export const HomeView: React.FC<HomeViewProps> = ({
  customerName,
  kanzlei,
  isPremium,
  matrixRoomId = '',
  elementSeat = false,
  recentItems,
  onNavigate,
  onRecentItemClick,
  onArkoModeSelect,
}) => {
  const light = usePortalAppearance() === 'light';
  const firstName = customerName?.trim() || 'willkommen';
  const lastDocItem = recentItems.find((i) => i.kind === 'document');
  const zuletztItems = portalHomeZuletztItems(recentItems, lastDocItem?.id ?? '');
  const matrixReady = hasMatrixRoom(matrixRoomId);

  const nextSteps: Array<{
    view: PortalView;
    label: string;
    description: string;
    icon: LucideIcon;
  }> = [
    {
      view: 'dokumente',
      label: 'Dokumente',
      description: 'Ordner anlegen, Dateien ablegen und im Editor mit ARKO bearbeiten.',
      icon: FolderOpen,
    },
    {
      view: 'diktat',
      label: 'Diktat',
      description: 'Mikrofon, Datei-Upload oder Element — Transkript und Protokoll folgen.',
      icon: Mic,
    },
  ];
  if (elementSeat) {
    nextSteps.push({
      view: 'matrix',
      label: PORTAL_NAV_MATRIX_LABEL,
      description: matrixReady
        ? 'Verschlüsselter Kanzlei-Raum — Sprachnachrichten und Protokolle über Element.'
        : 'Element-Raum wird beim Onboarding eingerichtet — bis dahin Diktat-Upload oder Anleitung.',
      icon: Users,
    });
  }

  const quickActions = [
    {
      key: 'last',
      label: lastDocItem ? lastDocItem.label : 'Dokumente',
      sub: lastDocItem ? 'Zuletzt geöffnet' : 'Alle Dateien',
      icon: lastDocItem ? FilePenLine : FolderOpen,
      accent: light
        ? 'text-blue-800 bg-blue-800/15 border-blue-800/60 hover:border-blue-800'
        : 'text-blue-400 bg-blue-500/10 border-blue-500/20 hover:border-blue-500/40',
      onClick: () => {
        if (lastDocItem) onRecentItemClick(lastDocItem);
        else onNavigate('dokumente');
      },
    },
    {
      key: 'arko',
      label: 'ARKO',
      sub: 'Am geöffneten Dokument',
      icon: MessageSquare,
      accent: light
        ? 'text-[#5b4678] bg-[#5b4678]/15 border-[#5b4678]/70 hover:border-[#5b4678] ring-1 ring-[#5b4678]/40'
        : 'text-violet-300 bg-violet-500/10 border-violet-500/20 hover:border-violet-500/40 ring-1 ring-violet-400/10',
      onClick: () => onNavigate('dokumente'),
    },
    {
      key: 'diktat',
      label: 'Diktat',
      sub: 'Aufnehmen oder hochladen',
      icon: Mic,
      accent: light
        ? 'text-[#0c6b4a] bg-[#0c6b4a]/15 border-[#0c6b4a]/60 hover:border-[#0c6b4a]'
        : 'text-emerald-400 bg-emerald-500/10 border-emerald-500/20 hover:border-emerald-500/40',
      onClick: () => onNavigate('diktat'),
    },
  ];

  const surface = light ? HOME_LIGHT_GROUND : HOME_GROUND;
  const edge = light ? HOME_LIGHT_EDGE : HOME_EDGE;
  const card = light ? HOME_LIGHT_CARD : HOME_CARD;
  const well = light ? HOME_LIGHT_WELL : 'border border-violet-400/30 bg-[#07070a]';
  const accentIcon = light ? `h-4 w-4 ${HOME_LIGHT_ACCENT}` : 'h-4 w-4 text-violet-300';
  const title = light ? HOME_LIGHT_TITLE : 'text-[#e6e7ea]';
  const nameTone = light ? HOME_LIGHT_TITLE : 'text-slate-300';
  const muted = light ? HOME_LIGHT_MUTED : PORTAL_SHELL.textMuted;
  const sectionTitle = light
    ? 'mb-3 text-xs font-semibold uppercase tracking-wider text-[#3f3832]'
    : 'mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500';
  const body = light ? 'text-sm font-medium text-[#1c1915]' : 'text-sm font-medium text-white';
  const hint = light ? 'mt-1 text-xs leading-relaxed text-[#3f3832]' : 'mt-1 text-xs leading-relaxed text-slate-500';
  const kicker = light
    ? 'text-[10px] font-semibold uppercase tracking-wider text-[#5b4678]'
    : 'text-[10px] font-semibold uppercase tracking-wider text-violet-400/80';
  const arkoLink = light
    ? `${HOME_LIGHT_ACCENT} underline-offset-2 hover:underline`
    : 'text-violet-400/90 underline-offset-2 hover:text-violet-300 hover:underline';
  const stepHint = light ? 'mt-0.5 text-xs leading-relaxed text-[#3f3832]' : 'mt-0.5 text-xs leading-relaxed text-slate-500';
  const chevron = light
    ? 'mt-1 h-4 w-4 flex-shrink-0 text-[#3f3832] opacity-0 transition-opacity group-hover:opacity-70'
    : 'mt-1 h-4 w-4 flex-shrink-0 text-slate-600 opacity-0 transition-opacity group-hover:opacity-70';
  const quickLabel = light ? 'truncate text-sm font-medium text-[#1c1915]' : 'truncate text-sm font-medium text-white';
  const quickSub = light ? 'mt-0.5 truncate text-xs text-[#3f3832]' : 'mt-0.5 truncate text-xs text-slate-500';
  const recentIcon = light ? 'mb-2 h-4 w-4 text-[#3f3832]' : 'mb-2 h-4 w-4 text-slate-500';
  const recentTitle = light ? 'truncate text-sm font-medium text-[#1c1915]' : 'truncate text-sm font-medium text-slate-200';
  const recentSub = light ? 'mt-0.5 truncate text-[11px] text-[#3f3832]' : 'mt-0.5 truncate text-[11px] text-slate-500';
  const disclaimer = light
    ? `${HOME_LIGHT_MUTED} border-t border-[#6a5684] pt-4 text-xs leading-relaxed`
    : `${PORTAL_SHELL.textMuted} border-t border-violet-400/20 pt-4 text-xs leading-relaxed`;

  return (
    <div className={`flex h-full min-h-0 w-full min-w-0 flex-1 flex-col ${portalHomeSurfaceClass(light ? 'light' : 'dark')} ${surface}`}>
      <div className={`flex min-h-0 w-full min-w-0 flex-1 flex-col overflow-y-auto p-3 md:p-4 ${edge}`}>
      <div className="flex w-full min-w-0 flex-col gap-6">
        <header className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2">
              <Sparkles className={light ? `h-4 w-4 ${HOME_LIGHT_ACCENT}` : 'h-4 w-4 text-violet-400'} aria-hidden />
              <span className={kicker}>
                Orbis Arbeitsplatz
              </span>
            </div>
            <h1 className={`text-xl font-semibold tracking-tight ${title}`}>
              Guten Tag, <span className={nameTone}>{firstName}</span>
            </h1>
            <p className={`${muted} mt-1 text-sm`}>{kanzlei}</p>
          </div>
          <PortalSnapshotStamps size="sm" className="flex-shrink-0" />
        </header>

        <section>
          <h2 className={sectionTitle}>
            Geführte Workflows
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {ARKO_HUB_MODES.map((mode) => {
              const Icon = mode.icon;
              return (
                <button
                  key={mode.id}
                  type="button"
                  onClick={() => {
                    onArkoModeSelect?.(mode);
                    dispatchArkoHubIntent(mode);
                  }}
                  className={card}
                >
                  <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${well}`}>
                    <Icon className={accentIcon} aria-hidden />
                  </div>
                  <p className={body}>{mode.label}</p>
                  <p className={hint}>{mode.description}</p>
                </button>
              );
            })}
          </div>
          <p className={`${muted} mt-3 text-[11px] leading-relaxed`}>
            Sie können einen Ablauf wählen oder direkt in ARKO arbeiten.{' '}
            <button
              type="button"
              onClick={() => onNavigate('chat')}
              className={arkoLink}
            >
              ARKO öffnen
            </button>
          </p>
        </section>

        <section>
          <h2 className={sectionTitle}>
            Nächste Schritte
          </h2>
          <p className={`${muted} mb-3 text-sm leading-relaxed`}>
            {elementSeat
              ? 'Dokumente, Diktat und Kanzlei-Chat — von hier aus.'
              : 'Dokumente und Diktat — von hier aus.'}
          </p>
          <ul className="space-y-2">
            {nextSteps.map(({ view, label, description, icon: Icon }) => (
              <li key={view}>
                <button
                  type="button"
                  onClick={() => onNavigate(view)}
                  className={`${card} flex w-full items-start gap-3`}
                >
                  <div className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${well}`}>
                    <Icon className={accentIcon} aria-hidden />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={body}>{label}</p>
                    <p className={stepHint}>{description}</p>
                  </div>
                  <ChevronRight className={chevron} />
                </button>
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h2 className={sectionTitle}>Schnellzugriff</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {quickActions.map(({ key, label, sub, icon: Icon, accent, onClick }) => (
              <button
                key={key}
                type="button"
                onClick={onClick}
                className={`group rounded-xl border p-4 text-left transition-colors ${accent}`}
              >
                <Icon className="mb-3 h-5 w-5 opacity-90" aria-hidden />
                <p className={quickLabel}>{label}</p>
                <p className={quickSub}>{sub}</p>
                <ChevronRight className="mt-2 h-4 w-4 opacity-0 transition-opacity group-hover:opacity-60" />
              </button>
            ))}
          </div>
        </section>

        {zuletztItems.length > 0 && (
          <section>
            <h2 className={sectionTitle}>Zuletzt</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {zuletztItems.map((item) => {
                const Icon = RECENT_ICON[item.kind];
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => onRecentItemClick(item)}
                    className={card}
                  >
                    <Icon className={recentIcon} aria-hidden />
                    <p className={recentTitle}>{item.label}</p>
                    <p className={recentSub}>{item.sublabel}</p>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        {!isPremium && (
          <p className={disclaimer}>
            Hinweis: ARKO unterstützt beim Formulieren und Zusammenfassen — keine Rechtsberatung.
          </p>
        )}
      </div>
      </div>
    </div>
  );
};
